import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Loader2,
  Play,
  Square,
  Coffee,
  ArrowRight,
  Wrench,
  ClipboardList,
  Cloud,
  FileDiff,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import LiveTimer from '@/components/clock/LiveTimer';
import {
  fetchActiveEntry,
  startClock,
  stopClock,
  startBreak,
  endBreak,
  formatUsd,
  VIRTUAL_JOBS,
  readCachedActiveEntry,
} from '@/lib/timeEntriesData';
import { translateRole, translateVirtualJobDesc } from '@/lib/i18nLabels';
import { toast } from 'sonner';

const statusBadge = {
  clocked_in:
    'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/40',
  on_break:
    'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40',
  idle: 'bg-[hsl(var(--muted))] text-muted-foreground border-border',
};

// Cross-device polling — every 15s we re-read Supabase so other devices'
// clock changes propagate (e.g. clock-in on phone shows up on desktop).
const POLL_MS = 15_000;

export const ClockCard = ({ crew, jobs = [], onChange }) => {
  const { t } = useTranslation();
  const statusLabel = {
    clocked_in: t('crewClock.on_the_clock'),
    on_break: t('crewClock.on_break'),
    idle: t('crewClock.off_the_clock'),
  };
  // Seed with cached entry for instant render; authoritative read happens on mount.
  const [entry, setEntry] = useState(() => readCachedActiveEntry(crew.id));
  const [pickedJobId, setPickedJobId] = useState('');
  const [pickedCoId, setPickedCoId] = useState('');
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(false);
  const mountedRef = useRef(true);

  const refresh = useCallback(
    async ({ silent = false } = {}) => {
      if (!silent) setSyncing(true);
      try {
        const next = await fetchActiveEntry(crew.id);
        if (!mountedRef.current) return;
        setEntry(next);
        setSyncError(false);
      } catch (e) {
        // Soft-fail: keep cached state, mark sync error so UI can hint.
        if (mountedRef.current) setSyncError(true);
      } finally {
        if (mountedRef.current && !silent) setSyncing(false);
      }
    },
    [crew.id]
  );

  // Initial load + polling for cross-device sync.
  useEffect(() => {
    mountedRef.current = true;
    refresh();
    const interval = setInterval(() => {
      refresh({ silent: true });
    }, POLL_MS);
    // Re-sync on tab focus (common multi-device scenario).
    const onFocus = () => refresh({ silent: true });
    window.addEventListener('focus', onFocus);
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [refresh]);

  const status = entry?.status || 'idle';

  // Picked job's billable Time & Material change orders (pending or approved).
  // Only real jobs carry change orders; virtual (SHOP/ADMIN) never do.
  const pickedJob = jobs.find((j) => j.id === pickedJobId);
  const tmCos = (
    Array.isArray(pickedJob?.change_orders) ? pickedJob.change_orders : []
  ).filter(
    (c) => (c?.type || 'fixed') === 'tm' && (c?.status || 'pending') !== 'denied'
  );

  const handlePickJob = (id) => {
    setPickedJobId(id);
    setPickedCoId(''); // reset CO scope whenever the job changes
  };

  // Combined options for the picker: virtual jobs first, then active jobs.
  const handleClockIn = async () => {
    if (!pickedJobId) {
      toast.error(t('crewClock.pick_job_first'));
      return;
    }
    const virtual = VIRTUAL_JOBS.find((v) => v.id === pickedJobId);
    const job = virtual || jobs.find((j) => j.id === pickedJobId);
    const co =
      !virtual && pickedCoId && pickedCoId !== '__none__'
        ? tmCos.find((c) => c.id === pickedCoId)
        : null;
    setBusy(true);
    try {
      const next = await startClock(crew.id, {
        jobId: virtual ? null : pickedJobId,
        jobName: job?.name || (virtual?.label ?? 'Active job'),
        coId: co?.id || null,
        coDesc: co?.desc || null,
      });
      setEntry(next);
      toast.success(
        t('crewClock.clocked_in_toast', {
          name: crew.name,
          job: job?.name || '',
        })
      );
      onChange?.();
    } catch (e) {
      toast.error(e?.message || t('crewClock.errors.clock_in'));
    } finally {
      setBusy(false);
    }
  };

  const handleClockOut = async () => {
    setBusy(true);
    try {
      const result = await stopClock(crew.id);
      if (!result) {
        // Nothing to close — refresh state defensively.
        await refresh();
        return;
      }
      setEntry(null);
      setPickedJobId('');
      setPickedCoId('');
      toast.success(
        t('crewClock.clocked_out_toast', {
          name: crew.name,
          hours: result.hours.toFixed(2),
          cost: formatUsd(result.cost),
        })
      );
      onChange?.();
    } catch (e) {
      toast.error(e?.message || t('crewClock.errors.clock_out'));
    } finally {
      setBusy(false);
    }
  };

  const handleBreak = async () => {
    setBusy(true);
    try {
      const next =
        status === 'on_break' ? await endBreak(crew.id) : await startBreak(crew.id);
      setEntry(next);
      toast.success(
        status === 'on_break'
          ? t('crewClock.break_ended_toast', { name: crew.name })
          : t('crewClock.break_started_toast', { name: crew.name })
      );
      onChange?.();
    } catch (e) {
      toast.error(e?.message || t('crewClock.errors.break'));
    } finally {
      setBusy(false);
    }
  };

  const VirtualIcon = ({ id }) =>
    id === '__SHOP__' ? (
      <Wrench size={12} className="text-[hsl(var(--primary))]" />
    ) : id === '__ADMIN__' ? (
      <ClipboardList size={12} className="text-[hsl(var(--primary))]" />
    ) : null;

  return (
    <Card
      className="relative border-[hsl(var(--hairline))] bg-[hsl(var(--card))] hover:border-[hsl(var(--primary))]/30 transition-colors overflow-hidden"
      data-testid={`clock-card-${crew.id}`}
    >
      <span
        aria-hidden
        className={cn(
          'metallic absolute left-0 top-0 h-full w-[2px]',
          status === 'clocked_in' && 'bg-[hsl(var(--success))]/70',
          status === 'on_break' && 'bg-[hsl(var(--warning))]/70',
          status === 'idle' && 'bg-border'
        )}
      />
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <Avatar className="h-12 w-12 border border-[hsl(var(--primary))]/30">
            <AvatarFallback className="bg-[hsl(var(--muted))] text-[hsl(var(--primary))] text-sm font-semibold">
              {crew.initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="text-base font-semibold text-foreground truncate"
                data-testid={`clock-card-name-${crew.id}`}
              >
                {crew.name}
              </span>
              <Badge
                variant="outline"
                className={cn(
                  'uppercase text-[10px] tracking-[0.16em]',
                  statusBadge[status]
                )}
                data-testid={`clock-card-status-${crew.id}`}
              >
                {statusLabel[status]}
              </Badge>
              {syncError ? (
                <span
                  title={t('crewClock.sync_failed_title')}
                  className="inline-flex items-center text-[10px] text-amber-700 dark:text-amber-300 gap-1"
                  data-testid={`clock-card-sync-error-${crew.id}`}
                >
                  <Cloud size={11} />
                  {t('crewClock.offline')}
                </span>
              ) : null}
            </div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {crew.type === 'sub' ? (
                <span
                  className="mr-1.5 inline-block rounded border border-[hsl(var(--primary))]/40 px-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[hsl(var(--primary))]"
                  data-testid={`clock-sub-badge-${crew.id}`}
                >
                  {t('crewClock.sub_badge', { defaultValue: 'Sub' })}
                </span>
              ) : null}
              {crew.type === 'sub' && crew.company
                ? crew.company
                : translateRole(t, crew.role)}{' '}
              · {formatUsd(crew.rate)}/hr
            </div>
          </div>
        </div>

        {/* Live timer */}
        <div
          className="mt-4 rounded-md border border-border bg-[hsl(var(--background))]/40 px-3.5 py-3 flex items-center justify-between gap-3"
          data-testid={`clock-card-timer-row-${crew.id}`}
        >
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {status === 'idle' ? t('crewClock.last_session') : t('crewClock.live')}
            </div>
            <LiveTimer
              entry={entry}
              className="text-2xl sm:text-3xl text-foreground"
              testId={`clock-timer-${crew.id}`}
            />
          </div>
          {entry?.job ? (
            <div className="text-right max-w-[180px]">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                {t('crewClock.job')}
              </div>
              <div className="text-sm font-semibold text-foreground truncate">
                {entry.job}
              </div>
              {entry.co_desc ? (
                <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-[hsl(var(--info))] max-w-[180px]">
                  <FileDiff size={10} className="shrink-0" />
                  <span className="truncate">{entry.co_desc}</span>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Job picker (only when idle) */}
        {status === 'idle' ? (
          <div className="mt-4 space-y-2">
          <div className="flex flex-col sm:flex-row items-stretch gap-2">
            <Select
              value={pickedJobId}
              onValueChange={handlePickJob}
              disabled={busy || syncing}
            >
              <SelectTrigger
                className="h-11 flex-1 bg-[hsl(var(--muted))] border-border text-foreground"
                data-testid={`clock-card-job-${crew.id}`}
              >
                <SelectValue placeholder={t('crewClock.select_job')} />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(var(--card))] border-border max-h-72">
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    {t('crewClock.internal_time')}
                  </SelectLabel>
                  {VIRTUAL_JOBS.map((v) => (
                    <SelectItem
                      key={v.id}
                      value={v.id}
                      data-testid={`clock-card-job-virtual-${v.id}`}
                    >
                      <span className="inline-flex items-center gap-2">
                        <VirtualIcon id={v.id} />
                        <span className="font-semibold">{v.label}</span>
                        <span className="text-xs text-muted-foreground">
                          — {translateVirtualJobDesc(t, v.id)}
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    {t('crewClock.active_jobs')}
                  </SelectLabel>
                  {jobs.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-muted-foreground">
                      {t('crewClock.no_active_jobs')}
                    </div>
                  ) : (
                    jobs.map((j) => (
                      <SelectItem key={j.id} value={j.id}>
                        {j.name} — {j.client}
                      </SelectItem>
                    ))
                  )}
                </SelectGroup>
              </SelectContent>
            </Select>
            <Button
              onClick={handleClockIn}
              disabled={busy || syncing || !pickedJobId}
              size="lg"
              className="h-11 sm:w-[170px] bg-[hsl(var(--success))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--success))]/90 uppercase tracking-[0.16em] text-xs"
              data-testid={`clock-in-${crew.id}`}
            >
              {busy ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play size={16} className="mr-2" />
              )}
              {t('actions.clock_in')}
            </Button>
          </div>

          {/* Optional CO scope — only when the picked job has T&M change orders */}
          {tmCos.length > 0 ? (
            <Select
              value={pickedCoId}
              onValueChange={setPickedCoId}
              disabled={busy || syncing}
            >
              <SelectTrigger
                className="h-10 w-full bg-[hsl(var(--muted))] border-border text-foreground"
                data-testid={`clock-card-co-${crew.id}`}
              >
                <SelectValue
                  placeholder={t('crewClock.co_placeholder', {
                    defaultValue: 'Base job — no change order',
                  })}
                />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(var(--card))] border-border max-h-72">
                <SelectItem value="__none__">
                  {t('crewClock.co_none', {
                    defaultValue: 'Base job — no change order',
                  })}
                </SelectItem>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    {t('crewClock.co_group', {
                      defaultValue: 'Time & Material change orders',
                    })}
                  </SelectLabel>
                  {tmCos.map((co) => (
                    <SelectItem key={co.id} value={co.id}>
                      <span className="inline-flex items-center gap-2">
                        <FileDiff size={12} className="text-[hsl(var(--info))]" />
                        <span className="truncate max-w-[220px]">{co.desc}</span>
                        {co.nte ? (
                          <span className="text-xs text-muted-foreground">
                            · cap {formatUsd(co.nte)}
                          </span>
                        ) : null}
                      </span>
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          ) : null}
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-2">
            <Button
              onClick={handleBreak}
              disabled={busy}
              size="lg"
              variant="outline"
              className={cn(
                'h-11 uppercase tracking-[0.16em] text-xs',
                status === 'on_break'
                  ? 'border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))] hover:bg-[hsl(var(--warning))]/10'
                  : 'border-[hsl(var(--primary))]/30 text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10'
              )}
              data-testid={`clock-break-${crew.id}`}
            >
              {busy ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : status === 'on_break' ? (
                <ArrowRight size={16} className="mr-2" />
              ) : (
                <Coffee size={16} className="mr-2" />
              )}
              {status === 'on_break' ? t('actions.end_break') : t('actions.start_break')}
            </Button>
            <Button
              onClick={handleClockOut}
              disabled={busy}
              size="lg"
              className="h-11 bg-[hsl(var(--destructive))] text-destructive-foreground hover:bg-[hsl(var(--destructive))]/90 uppercase tracking-[0.16em] text-xs"
              data-testid={`clock-out-${crew.id}`}
            >
              {busy ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Square size={16} className="mr-2" />
              )}
              {t('actions.clock_out')}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ClockCard;
